import { faSearch, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { BZ2_SEARCH_BUTTON, FACET_SELECT_NO_OPTIONS } from 'mk/autogenerated/translations/FacetSelect.16d351e115c411b4a4fc0892bf1cd4ee'
import PropertyListItem from 'mk2/apps/bazaar/components/PropertyListItem';
import useFacetFiltering from 'mk2/apps/bazaar/hooks/useFacetFiltering';
import { ClearableInput } from 'mk2/components/ClearableInput';
import { InfiniteListLoadable } from 'mk2/components/InfiniteListLoadable';
import { Input } from 'mk2/components/Input';
import React, { useCallback } from 'react';
import { ListRowProps } from 'react-virtualized';
import styles from './FacetSelect.mscss';

export interface PropertyOption {
    label: React.ReactNode;
    value: string | number;
    filterValue: string;
    disabled?: boolean;
    count?: number;
}

const ROW_HEIGHT = 44;
const isRowLoaded = () => true;
const loadMoreRows = () => null; // Noop

interface OwnProps {
    className?: string;
    property: string;
    options: PropertyOption[];
    isMobile: boolean;
    label: React.ReactNode;
    hasFiltering: boolean;
    alwaysDisplayed?: Array<PropertyOption['value']>;

    isSelected(option: PropertyOption): boolean;
    onItemClick(property: string, value: string);
}

type Props = OwnProps;

const FacetSelect: React.FunctionComponent<Props> = ({
    className,
    hasFiltering,
    options,
    isMobile,
    label,
    property,
    isSelected,
    alwaysDisplayed,
    onItemClick,
    children,
}) => {
    const { filterValue, options: filteredOptions, onFilterClear, onFilterChange } = useFacetFiltering({
        options,
        alwaysDisplayed,
        isSelected,
    });

    const rowRenderer = useCallback(
        ({ index, key, style }: ListRowProps) => {
            const option = filteredOptions[index];
            return (
                <PropertyListItem
                    key={key}
                    style={style}
                    isLast={index + 1 === filteredOptions.length}
                    onClick={option.disabled ? null : onItemClick}
                    property={property}
                    value={option.value}
                    label={option.label}
                    count={option.count}
                    selected={isSelected(option)}
                />
            );
        },
        [filteredOptions, property, onItemClick, isSelected],
    );

    return (
        <div className={cx(styles.FacetSelect, className)}>
            {hasFiltering && (
                <ClearableInput
                    className={styles.FacetSelect__filter}
                    icon={<FontAwesomeIcon icon={faSearch} />}
                    clearIcon={<FontAwesomeIcon icon={faTimesCircle} className={styles.FacetSelect__clearIcon} />}
                    hasValue={Boolean(filterValue)}
                    onClear={onFilterClear}
                >
                    <Input
                        className={styles.FacetSelect__filter__input}
                        type="text"
                        onChange={onFilterChange}
                        placeholder={BZ2_SEARCH_BUTTON + '...'}
                        value={filterValue}
                        autoFocus={!isMobile}
                        autoScrollIntoView={!isMobile}
                        autoScrollIntoViewOffset={-111}
                        data-cy="facet-select-filter"
                    />
                </ClearableInput>
            )}

            {children}

            <div className={styles.FacetSelect__headline}>
                <span className={styles.FacetSelect__headline__text}>{label}</span>
            </div>

            <div className={styles.FacetSelect__options}>
                {options.length === 0 ? (
                    <div className={styles.FacetSelect__options__empty}>{FACET_SELECT_NO_OPTIONS}</div>
                ) : (
                    <InfiniteListLoadable
                        isRowLoaded={isRowLoaded}
                        loadMoreRows={loadMoreRows}
                        rowCount={filteredOptions.length}
                        rowHeight={ROW_HEIGHT}
                        rowRenderer={rowRenderer}
                        numberOfItemsOnFirstPage={15}
                    />
                )}
            </div>
        </div>
    );
};

FacetSelect.displayName = 'FacetSelect';

export default React.memo<React.PropsWithChildren<Props>>(FacetSelect);
